import { useState, type FormEvent } from 'react';
import { Link, useActionData, useLoaderData, useNavigation, useSubmit } from 'react-router-dom';
import { Button, FormField, Input, Textarea } from '@dma/ui';
import { ImageUploadField } from '../../media';
import type { ArticleCategory, ArticleStatus } from '../domain/entities/Article.entity';
import type { ArticleDto } from '../application/dto/ArticleDto';
import type { SaveArticleDto } from '../application/dto/SaveArticleDto';
import './AdminArticleFormPage.css';

export interface AdminArticleFormLoaderData {
  heading: string;
  basePath: string;
  category: ArticleCategory;
  /** null in "new" mode, the existing article in "edit" mode. */
  article: ArticleDto | null;
}

interface ActionFailure {
  ok: false;
  message: string;
}

function toFormState(article: ArticleDto | null, category: ArticleCategory) {
  return {
    title: article?.title ?? '',
    slug: article?.slug ?? '',
    summary: article?.summary ?? '',
    body: article?.body ?? '',
    coverImage: article?.coverImage ?? '',
    // <input type="date"> wants YYYY-MM-DD, not a full ISO datetime string.
    publishedAtDate: article?.publishedAt ? article.publishedAt.slice(0, 10) : '',
    status: article?.status ?? ('draft' as ArticleStatus),
    category
  };
}

/**
 * Used for both `/admin/news/new` + `/admin/news/:id/edit` (and the
 * `/admin/articles` equivalents) — same create-vs-edit-by-loader pattern as
 * AdminScientificEntryFormPage, but a plain controlled form with no dynamic
 * repeatable field lists (an article has one cover image, not a gallery),
 * so this one is noticeably shorter.
 */
export function AdminArticleFormPage() {
  const { heading, basePath, category, article } = useLoaderData() as AdminArticleFormLoaderData;
  const actionData = useActionData() as ActionFailure | undefined;
  const navigation = useNavigation();
  const submit = useSubmit();
  const isSubmitting = navigation.state === 'submitting';

  const [form, setForm] = useState(() => toFormState(article, category));

  function updateField<K extends keyof ReturnType<typeof toFormState>>(
    key: K,
    value: ReturnType<typeof toFormState>[K]
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const payload: SaveArticleDto = {
      id: article?.id ?? null,
      slug: form.slug,
      title: form.title,
      category: form.category,
      summary: form.summary,
      body: form.body,
      coverImage: form.coverImage.trim() === '' ? null : form.coverImage,
      publishedAt: form.publishedAtDate.trim() === '' ? null : new Date(form.publishedAtDate).toISOString(),
      status: form.status
    };

    submit(payload as unknown as Record<string, unknown>, { method: 'post', encType: 'application/json' });
  }

  return (
    <div className="dma-admin-article-form">
      <Link to={basePath} className="dma-admin-article-form__back">
        ← بازگشت به فهرست
      </Link>
      <h1>{heading}</h1>

      <form onSubmit={handleSubmit} noValidate>
        {actionData && actionData.ok === false ? (
          <p className="dma-admin-article-form__error" role="alert">
            {actionData.message}
          </p>
        ) : null}

        <FormField label="عنوان" htmlFor="title" required>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            required
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label="اسلاگ (فقط حروف انگلیسی کوچک و خط تیره، برای آدرس صفحه)" htmlFor="slug" required>
          <Input
            id="slug"
            value={form.slug}
            onChange={(e) => updateField('slug', e.target.value)}
            placeholder="new-mining-license"
            required
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label="خلاصه" htmlFor="summary" required>
          <Textarea
            id="summary"
            rows={3}
            value={form.summary}
            onChange={(e) => updateField('summary', e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label="متن کامل (هر خط جدید یک پاراگراف جدا می‌شود)" htmlFor="body" required>
          <Textarea
            id="body"
            rows={12}
            value={form.body}
            onChange={(e) => updateField('body', e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>
        <p className="dma-admin-article-form__link-hint">
          برای لینک‌دادن به یک کلمه، این‌طور بنویسید: <code>[کلسیت](/mineralogy/calcite)</code> — کلمه‌ی
          داخل کروشه به همان صفحه لینک می‌شود. برای لینک به صفحه‌ی دیگری از همین سایت با{' '}
          <code>/</code> شروع کنید، مثل <code>[اخبار کلسیت](/news/calcite-news)</code>.
        </p>

        <ImageUploadField
          label="تصویر شاخص"
          fieldId="coverImage"
          value={form.coverImage}
          onChange={(url) => updateField('coverImage', url)}
          folder="articles"
          disabled={isSubmitting}
        />

        <FormField label="تاریخ انتشار (اختیاری)" htmlFor="publishedAtDate">
          <Input
            id="publishedAtDate"
            type="date"
            value={form.publishedAtDate}
            onChange={(e) => updateField('publishedAtDate', e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        <div className="dma-admin-article-form__status-options">
          <label>
            <input
              type="radio"
              name="status"
              value="draft"
              checked={form.status === 'draft'}
              onChange={() => updateField('status', 'draft')}
              disabled={isSubmitting}
            />
            پیش‌نویس (فقط در پنل مدیریت قابل مشاهده است)
          </label>
          <label>
            <input
              type="radio"
              name="status"
              value="published"
              checked={form.status === 'published'}
              onChange={() => updateField('status', 'published')}
              disabled={isSubmitting}
            />
            منتشرشده (روی سایت عمومی نمایش داده می‌شود)
          </label>
        </div>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'در حال ذخیره...' : 'ذخیره'}
        </Button>
      </form>
    </div>
  );
}
